<template>
	<div class="navigationBar py-03">
		<NavigationBarPlaceholder v-if="shouldShowPlaceholder" />

		<div v-else>
			<!--
		TODO: Decide if we still want to keep this card. If yes - where to put it?
		https://indiebi.atlassian.net/browse/DA-353
		<GuideCard />
		 -->

			<div class="flex items-center justify-between">
				<div class="left flex items-center gap-05">
					<ButtonGroupToggle
						v-model="timelineResolutionPreset"
						:values="timelineResolutionPresets"
						size="s"
						class="resolutionPresetButtons"
						@update:model-value="setTimelineResolutionPreset"
					/>

					<Tooltip :content="t('navigation.productsTooltip')">
						<Button icon="star-outline" size="s" variant="quaternary" :disabled="isLoading" @click="showFavouriteSKUs()">
							<div class="flex items-center gap-03">
								{{ t('navigation.products') }}

								<div class="bg-level-04 p-02">{{ skusFlat.length }}</div>
							</div>
						</Button>
					</Tooltip>

					<Tooltip
						:content="scorePeriod ? t('navigation.scoreButtonTooltip', { period: scorePeriod }) : t('navigation.scoreButtonTooltipNoPeriod')"
					>
						<ScoreButton :score="summary.score" :is-loading="isLoadingSaleDaysSummary" @click="showSaleDaysSummary" />
					</Tooltip>

					<ActionsButton />

					<Button v-if="hasDiscountSettersEnabled" size="s" @click="showDiscountSettersModal">{{ t('setters.sendEventsCTA') }}</Button>

					<Button v-else size="s" variant="tangerine-secondary" icon="creation" @click="openAutomationUpsellModal">{{ t('automation') }}</Button>
				</div>

				<div class="right flex items-center justify-end gap-03">
					<Tooltip>
						<Switch
							:model-value="isUsingStoreSpecificTimeZones"
							:label="t('navigation.useStoreSpecificTimeZone')"
							size="small"
							@update:model-value="toggleUsingStoreSpeficicTimeZones"
						/>

						<template #content>
							<T path="navigation.useStoreSpecificTimeZoneTooltip">
								<template #localTimeZone> {{ localTimeZoneName }} </template>
							</T>
						</template>
					</Tooltip>

					<Tooltip :content="t('navigation.notesOnlySwitchTooltip')">
						<Switch
							:model-value="isShowingNotesOnly"
							:label="t('navigation.notesOnlySwitch')"
							size="small"
							@update:model-value="toggleShowingOnlyEventsWithNotes"
						/>
					</Tooltip>

					<Tooltip :content="t('navigation.scheduledOnlySwitchTooltip')">
						<Switch
							:model-value="isShowingScrapedOnly"
							:label="t('navigation.scheduledOnlySwitch')"
							size="small"
							@update:model-value="toggleShowingOnlyScrapedEvents"
						/>
					</Tooltip>

					<!-- This button is hidden for now as there is still no design for their actions -->
					<Tooltip v-if="false" :content="t('navigation.scrapeDataTooltip')">
						<Button variant="quaternary" icon="refresh" size="s" />
					</Tooltip>

					<Tooltip :content="t('navigation.legendTooltip')">
						<Button variant="quaternary" icon="information-outline" size="s" @click="showEventTypeLegend" />
					</Tooltip>

					<!-- This button is hidden for now as there is still no design for their actions -->
					<Tooltip v-if="false" :content="t('navigation.conflictsTooltip')">
						<Button variant="quaternary" icon="format-list-bulleted-square" size="s" />
					</Tooltip>

					<Tooltip :content="t('navigation.reloadDataTooltip')">
						<Button variant="quaternary" :loading="isLoading" icon="reload" size="s" @click="emit('reload')" />
					</Tooltip>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { Button, ButtonGroupToggle, useTranslation, Switch, Tooltip } from '@bluebrick/saas-ui'
import { DateTime } from 'luxon'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import ActionsButton from '@/features/actions/components/ActionsButton.vue'
import { useDiscountSettersStore } from '@/features/discountSetters/useDiscountSettersStore'
import NavigationBarPlaceholder from '@/features/navigation/components/NavigationBarPlaceholder.vue'
import { type Preferences } from '@/features/preferences/types'
import { usePreferencesStore } from '@/features/preferences/usePreferencesStore'
import ScoreButton from '@/features/saleDaysSummary/components/ScoreButton.vue'
import { useScorePeriods } from '@/features/saleDaysSummary/composables/useScorePeriods'
import { useSaleDaysSummaryStore } from '@/features/saleDaysSummary/useSaleDaysSummaryStore'
import { useSidebarStore } from '@/features/sidePanel/useSidebarStore'
import { useSkusStore } from '@/features/skus/useSkusStore'
import { useUpsellStore } from '@/features/upsell/useUpsellStore'
import { useFeatureFlagStore } from '@/features/user/useFeatureFlagStore'
import T from '@/translations/T.vue'

const { timelineResolutionPreset, isShowingNotesOnly, isShowingScrapedOnly, isUsingStoreSpecificTimeZones } = storeToRefs(usePreferencesStore())
const { setTimelineResolutionPreset, toggleShowingOnlyEventsWithNotes, toggleShowingOnlyScrapedEvents, toggleUsingStoreSpeficicTimeZones } =
	usePreferencesStore()
const { t } = useTranslation()
const { showEventTypeLegend, showFavouriteSKUs, showSaleDaysSummary } = useSidebarStore()
const { skusFlat } = storeToRefs(useSkusStore())
const { summary, isLoading: isLoadingSaleDaysSummary, selectedPeriod } = storeToRefs(useSaleDaysSummaryStore())
const { openAutomationUpsellModal } = useUpsellStore()
const { periods } = useScorePeriods()
const { showDiscountSettersModal } = useDiscountSettersStore()
const { hasDiscountSettersEnabled } = useFeatureFlagStore()

const timelineResolutionPresets = ref([
	{ label: t('timelineResolution.days'), value: 'daily' as Preferences['timelineResolutionPreset'] },
	{ label: t('timelineResolution.months'), value: 'monthly' as Preferences['timelineResolutionPreset'] }
])

defineProps<{
	isLoading: boolean
	shouldShowPlaceholder: boolean
}>()

const emit = defineEmits<{
	reload: [void]
}>()

const scorePeriod = computed(() => {
	if (!summary.value.startDate.isValid || !summary.value.endDate.isValid || !selectedPeriod.value) {
		return ''
	}

	return periods.value.items?.find((period) => period.id === selectedPeriod.value)?.name ?? ''
})

const localTimeZoneName = computed(() => DateTime.now().offsetNameShort)
</script>

<style lang="scss" scoped>
.navigationBar {
	.resolutionPresetButtons {
		:deep(.button) {
			padding-left: theme('spacing.05');
			padding-right: theme('spacing.05');
		}
	}
}
</style>
